import { useContext, useMemo } from 'react';

import { UserContext } from '../../../App';
import i18n, { getLanguageWithRegionTag } from '../../../i18n/i18n';
import { EXECUTION_STATUS } from '../../../interfaces/enums';
import { type ExecutionForReact, type ExecutionOutcome, RIGHTS } from '../../../interfaces/Interfaces';
import { type ColumnDefinition, ColumnModifier, StaticDatasource, Table } from '../../../lib/Table';
import ExecutionStateIndicator from '../ExecutionStateIndicator/ExecutionStateIndicator';

function ExecutionOutcomeDetails(props: Readonly<{ item: ExecutionForReact }>) {
  const wasRetriedExecution = !!props.item.lastModified;
  const isManualExecution = props.item.manual;
  const userContext = useContext(UserContext);

  function getRuleName(ruleName: string, isManualExecution: boolean): string {
    if (isManualExecution) {
      return i18n.t('action-page.details.rule-manual-change');
    }
    if (ruleName === '' || ruleName === undefined) return i18n.t('action-page.details.rule-archived');
    return ruleName;
  }

  const outcomeTableCols: Array<ColumnDefinition<ExecutionOutcome>> = useMemo(
    () => [
      {
        accessor: item => item.executionState,
        renderer: (value, item) => <ExecutionStateIndicator state={item.executionState} />,
        headerName: '',
        modifiers: [ColumnModifier.FIT_CONTENT],
      },
      {
        accessor: item => item.actionInfo,
        headerName: i18n.t('action-page.details.action'),
      },
      {
        accessor: item => item.contractIds.join(', '),
        headerName: i18n.t('action-page.details.contract'),
      },
      {
        accessor: item => getRuleName(item.ruleName, isManualExecution),
        headerName: i18n.t('action-page.details.rule'),
      },
      ...(userContext.rights.includes(RIGHTS.EXTENDED_ACTION_INFO)
        ? ([
            {
              accessor: item => item.receivableIds.join(','),
              headerName: i18n.t('action-page.details.receivableIds'),
            },
          ] as Array<ColumnDefinition<ExecutionOutcome>>)
        : []),
      ...(wasRetriedExecution
        ? ([
            {
              accessor: item => item.lastModified,
              renderer: value =>
                (typeof value === 'string' && new Date(value).toLocaleString(getLanguageWithRegionTag())) || '-',
              headerName: i18n.t('action-page.details.retried-at'),
            },
            {
              accessor: item => item.lastModifiedUser || '-',
              headerName: i18n.t('action-page.details.retried-by'),
            },
          ] as Array<ColumnDefinition<ExecutionOutcome>>)
        : []),
    ],
    [wasRetriedExecution, isManualExecution],
  );

  return (
    <div className="execution-outcome-details">
      {props.item.executionState == EXECUTION_STATUS.CANCELED && (
        <>
          <b>{i18n.t('action-page.details.cancelled')}</b>
          <div className="cancellation-details">
            <div>
              <span>{i18n.t('action-page.details.cancellation-date')} </span>
              <span>{i18n.t('action-page.details.cancellation-user')} </span>
            </div>
            <div>
              <span>{new Date(props.item.lastModified).toLocaleString(getLanguageWithRegionTag())}</span>
              <span>{props.item.lastModifiedUser}</span>
            </div>
          </div>
        </>
      )}
      {wasRetriedExecution && (
        <div className="retry-info">
          {i18n.t('action-page.details.retry-info', {
            date: new Date(props.item.lastModified).toLocaleString(getLanguageWithRegionTag()),
            user: props.item.lastModifiedUser,
          })}
        </div>
      )}
      {isManualExecution && (
        <div className="manual-level-change-info">
          {i18n.t('action-page.details.manual-level-change-info', {
            date: new Date(props.item.timestamp).toLocaleString(getLanguageWithRegionTag()),
            user: props.item.lastModifiedUser,
          })}
        </div>
      )}
      <b>{i18n.t('action-page.details.title')}</b>
      <Table variant="light" dataSource={new StaticDatasource(props.item.outcomes)} columns={outcomeTableCols} />
    </div>
  );
}

export default ExecutionOutcomeDetails;
